import * as THREE from 'three';
import type { EntityCtx } from '../Entity';
import { Bullet } from './Bullet';

/**
 * A reusable defensive gun turret bolted onto a host (zeppelin, sky carrier, iron worm). It
 * tracks the player-controlled unit, swivels its barrels to bear, and fires enemy bullets on a
 * cooldown while the target is in range. The `mount` is added as a child of the host's group at a
 * local offset; the host calls update() each frame while alive (and stops once it starts dying).
 *
 * The barrels are built along +Z so Object3D.lookAt — which points +Z at the target — aims them.
 */
export interface TurretOpts {
  range: number;       // max engagement distance
  interval: number;    // seconds between shots
  speed: number;       // bullet speed (u/s)
  spread?: number;     // aim scatter in radians-ish (default 0.05)
  size?: number;       // mesh scale (default 1)
  bulletScale?: number; // visual bullet size (default 1)
}

const METAL = new THREE.MeshStandardMaterial({ color: 0x2c2e30, roughness: 0.5, metalness: 0.6, flatShading: true });
const GUN = new THREE.MeshStandardMaterial({ color: 0x16181a, roughness: 0.6, metalness: 0.5, flatShading: true });

export class Turret {
  readonly mount = new THREE.Group();
  /** Host-adjustable fire-rate multiplier (<1 = faster) — lets bosses enrage their guns. */
  intervalScale = 1;
  private cd: number;
  private readonly opts: Required<TurretOpts>;
  private readonly barrelLen: number;
  private flip = 0; // alternate the twin barrels

  constructor(opts: TurretOpts) {
    this.opts = { spread: 0.05, size: 1, bulletScale: 1, ...opts };
    // stagger the first shot a little so multiple turrets don't fire in lockstep
    this.cd = Math.random() * opts.interval;
    const s = this.opts.size;
    // Swivel base + receiver block.
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.55 * s, 0.75 * s, 0.4 * s, 10), METAL);
    base.position.y = 0.2 * s;
    this.mount.add(base);
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.7 * s, 0.5 * s, 0.7 * s), METAL);
    body.position.y = 0.55 * s;
    this.mount.add(body);
    // Twin barrels along +Z (the lookAt axis).
    this.barrelLen = 1.5 * s;
    for (const dx of [-0.16, 0.16]) {
      const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.08 * s, 0.09 * s, this.barrelLen, 8), GUN);
      barrel.rotation.x = Math.PI / 2;           // Y-axis cylinder → lie along +Z
      barrel.position.set(dx * s, 0.55 * s, this.barrelLen * 0.5);
      this.mount.add(barrel);
    }
  }

  /** Attach this turret to a host group at a local offset. */
  attachTo(host: THREE.Object3D, x: number, y: number, z: number) {
    this.mount.position.set(x, y, z);
    host.add(this.mount);
  }

  /** Track + fire at the NEAREST friendly unit in range — player, wingman, tank or AA gun alike,
   *  so pushing your force onto a boss draws its fire. Pass the host's id as owner so the
   *  turret's bullets aren't attributed to the player. `enabled` lets the host gate fire
   *  (e.g. the Iron Worm only shoots while breached). */
  update(ctx: EntityCtx, ownerId: number, enabled = true) {
    if (!enabled) return;
    this.mount.updateWorldMatrix(true, false);
    const mp = new THREE.Vector3();
    this.mount.getWorldPosition(mp);
    const target = ctx.game.nearestFriendly(mp, this.opts.range);
    if (!target) return;
    const tp = target.position;

    // Aim the barrels at the target (Object3D.lookAt points +Z at it; handles the parent transform).
    this.mount.lookAt(tp);

    this.cd -= ctx.dt;
    if (this.cd > 0) return;
    this.cd = this.opts.interval * this.intervalScale;
    const dir = tp.clone().sub(mp).normalize();
    // Fire from the active barrel of the twin pair, alternating.
    this.flip ^= 1;
    const right = new THREE.Vector3().crossVectors(dir, new THREE.Vector3(0, 1, 0)).normalize();
    const muzzle = mp.clone().addScaledVector(dir, this.barrelLen).addScaledVector(right, (this.flip ? 0.16 : -0.16) * this.opts.size);
    const vel = dir.clone();
    vel.x += (Math.random() - 0.5) * this.opts.spread;
    vel.y += (Math.random() - 0.5) * this.opts.spread;
    vel.z += (Math.random() - 0.5) * this.opts.spread;
    vel.normalize().multiplyScalar(this.opts.speed);
    const b = new Bullet(muzzle, vel, ownerId, 'enemy');
    ctx.game.add(b);
  }
}
